import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

const HostelLeaveLevel1 = ({ onNavigate, userInfo }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [remarks, setRemarks] = useState('');
  const [processing, setProcessing] = useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userPrivileges, setUserPrivileges] = useState([]);
  const [privilegesLoading, setPrivilegesLoading] = useState(true);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchDashboardData();
      fetchUserPrivileges();
      fetchApplications();
    }
  }, [userInfo]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/dashboard/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  const fetchUserPrivileges = async () => {
    try {
      setPrivilegesLoading(true);
      const response = await fetch(`${API_URL}/admin/user-privileges/${userInfo.userId}`);
      const data = await response.json();

      if (data.success && data.privileges) {
        setUserPrivileges(data.privileges);
      } else {
        setUserPrivileges([]);
      }
    } catch (err) {
      console.error('Error fetching privileges:', err);
      setUserPrivileges([]);
    } finally {
      setPrivilegesLoading(false);
    }
  };

  const getPrivilegesByCategory = (category) => {
    return userPrivileges.filter(privilege => privilege.page_category === category);
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/admin/leave-applications/level1`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setApplications(data.applications || []);
      } else {
        setError(data.message || 'Failed to load applications');
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.userId) {
      const timer = setTimeout(() => {
        fetchApplications();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setDetailsModalOpen(true);
  };

  const openActionModal = (application, action) => {
    setSelectedApplication(application);
    setActionType(action);
    setRemarks('');
    setActionModalOpen(true);
  };

  const closeActionModal = () => {
    setActionModalOpen(false);
    setSelectedApplication(null);
    setActionType('');
    setRemarks('');
  };

  const handleAction = async () => {
    if (!remarks.trim()) {
      alert('Remarks are required');
      return;
    }

    try {
      setProcessing(true);
      const endpoint = actionType === 'approve'
        ? `/admin/leave-applications/${selectedApplication.id}/approve-level1`
        : `/admin/leave-applications/${selectedApplication.id}/reject-level1`;

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ remarks })
      });

      const data = await response.json();
      if (data.success) {
        alert(`Application ${actionType === 'approve' ? 'approved and forwarded to Level 2' : 'rejected'}`);
        closeActionModal();
        fetchApplications();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      alert('Error processing request: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const MenuItem = ({ emoji, label, active = false, route }) => (
    <div
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setMobileMenuOpen(false);
      }}
    >
      <span className="menu-icon">{emoji}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const handleLogout = () => {
    sessionStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };
  const handleDownloadAttachment = async (leaveId, fileName) => {
    try {
      const token = localStorage.getItem('token');

      // Create a temporary link and trigger download
      const url = `${API_URL}/admin/leave/download/${leaveId}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.message || 'Failed to download attachment');
        return;
      }

      // Get the blob
      const blob = await response.blob();

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName || 'attachment';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log('✅ Download successful');
    } catch (error) {
      console.error('❌ Download error:', error);
      alert('Failed to download attachment');
    }
  };

  const handleViewAttachment = (leaveId) => {
    // Open in new tab to view
    const url = `${API_URL}/admin/leave/view/${leaveId}`;
    window.open(url, '_blank');
  };
  const filteredApplications = applications.filter(app =>
    app.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.roll_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.leave_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredApplications.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentPageApplications = filteredApplications.slice(startIndex, startIndex + rowsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && applications.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(43,103,119,0.15)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f2f8f7',
            borderTop: '4px solid #2b6777',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: '#2b6777',
            fontSize: '16px',
            fontWeight: '600',
            margin: 0
          }}>Loading Leave Applications...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: 'linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%)';
          overflow-x: hidden;
        }

        .dashboard-container {
          min-height: 100vh;
          display: flex;
          position: relative;
        }

        .sidebar {
          position: fixed;
          left: 0;
          top: 0;
          width: 280px;
          height: 100vh;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          box-shadow: 4px 0 24px rgba(43,103,119,0.1);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          border-right: 2px solid rgba(82,171,152,0.15);
          transition: transform 0.3s ease;
        }

        .mobile-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 999;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s;
        }

        .mobile-overlay.show {
          opacity: 1;
          pointer-events: all;
        }

        .sidebar-header {
          padding: 28px 22px;
          background: linear-gradient(135deg, rgba(242,248,247,0.8) 0%, rgba(199,229,225,0.6) 100%);
          border-bottom: 2px solid rgba(82,171,152,0.15);
        }

        .sidebar-top {
          display: none;
          justify-content: flex-end;
          margin-bottom: 20px;
        }

        .sidebar-close-btn {
          background: rgba(43,103,119,0.08);
          border: 1.5px solid rgba(82,171,152,0.25);
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .sidebar-close-btn:hover {
          background: rgba(43,103,119,0.15);
          border-color: #52ab98;
          transform: rotate(90deg);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-avatar-sidebar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 22px;
          color: white;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          border: 3px solid rgba(255, 255, 255, 0.5);
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .sidebar-nav {
          flex: 1;
          padding: 28px 20px;
          overflow-y: auto;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .nav-section {
          margin-bottom: 32px;
        }

        .nav-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: rgba(43,103,119,0.5);
          margin-bottom: 12px;
          padding-left: 18px;
        }

        .menu-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          margin-bottom: 10px;
          cursor: pointer;
          color: #2b6777;
          font-size: 15px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s;
          overflow: hidden;
        }

        .menu-glow {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .menu-item:hover {
          color: #52ab98;
          background: rgba(82,171,152,0.1);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .menu-item:hover .menu-glow {
          opacity: 1;
        }

        .menu-item-active {
          background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
          color: #2b6777;
          box-shadow: 0 6px 20px rgba(82,171,152,0.2);
          font-weight: 700;
        }

        .menu-item-active .menu-glow {
          opacity: 1;
          animation: glow 2s ease-in-out infinite;
        }

        .menu-icon {
          font-size: 22px;
          position: relative;
          z-index: 1;
        }

        .menu-label {
          position: relative;
          z-index: 1;
        }

        .logout-section {
          padding: 20px;
          border-top: 2px solid rgba(82,171,152,0.15);
        }

        .logout-btn {
          width: 100%;
          padding: 16px 20px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .logout-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(43,103,119,0.4);
        }

        .main-content {
          flex: 1;
          margin-left: 280px;
          padding: 20px;
          width: calc(100% - 280px);
        }

        .top-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          padding: 18px 24px;
          border-radius: 18px;
          box-shadow: 0 6px 24px rgba(43,103,119,0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          border: 2px solid rgba(82,171,152,0.12);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .mobile-menu-toggle {
          display: none;
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border: 2px solid rgba(82,171,152,0.2);
          cursor: pointer;
          font-size: 22px;
          color: #2b6777;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .mobile-menu-toggle:hover {
          background: linear-gradient(135deg, rgba(43,103,119,0.2) 0%, rgba(82,171,152,0.2) 100%);
          border-color: #52ab98;
          transform: scale(1.05);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .header-greeting {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .header-name {
          font-size: 20px;
          font-weight: 800;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-avatar-header {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 16px;
          color: white;
          box-shadow: 0 6px 20px rgba(43,103,119,0.25);
          border: 3px solid rgba(255, 255, 255, 0.8);
        }

        .page-title-section {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 24px;
          padding: 40px 36px;
          margin-bottom: 28px;
          box-shadow: 0 12px 48px rgba(43,103,119,0.3);
          position: relative;
          overflow: hidden;
        }

        .page-title-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          filter: blur(60px);
        }

        .page-title-content {
          position: relative;
          z-index: 1;
        }

        .page-title {
          font-size: 28px;
          font-weight: 900;
          color: white;
          margin-bottom: 10px;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }

        .search-section {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .search-input {
          flex: 1;
          padding: 14px 20px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-family: inherit;
          transition: all 0.3s;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .applications-table-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .table-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .pagination-info {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .table-count {
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          color: #2b6777;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          border: 2px solid rgba(82,171,152,0.2);
        }

        .applications-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 12px;
        }

        .applications-table thead th {
          background: linear-gradient(135deg, rgba(242,248,247,0.8) 0%, rgba(199,229,225,0.6) 100%);
          padding: 16px;
          text-align: left;
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: none;
        }

        .applications-table thead th:first-child {
          border-radius: 12px 0 0 12px;
        }

        .applications-table thead th:last-child {
          border-radius: 0 12px 12px 0;
        }

        .applications-table tbody tr {
          background: rgba(242,248,247,0.5);
          transition: all 0.3s;
          border-radius: 12px;
        }

        .applications-table tbody tr:hover {
          background: rgba(242,248,247,0.8);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .applications-table tbody td {
          padding: 18px 16px;
          font-size: 14px;
          color: #2b6777;
          border: none;
        }

        .applications-table tbody td:first-child {
          border-radius: 12px 0 0 12px;
        }

        .applications-table tbody td:last-child {
          border-radius: 0 12px 12px 0;
        }

        .student-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .student-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 14px;
          color: white;
        }

        .student-details {
          display: flex;
          flex-direction: column;
        }

        .student-name {
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 2px;
        }

        .student-id {
          font-size: 12px;
          color: rgba(43,103,119,0.6);
        }

        .action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          background: linear-gradient(135deg, rgba(52,172,224,0.15) 0%, rgba(52,172,224,0.25) 100%);
          color: #34ace0;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .approve-btn {
          background: linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(34,197,94,0.25) 100%);
          color: #22c55e;
        }

        .reject-btn {
          background: linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(239,68,68,0.25) 100%);
          color: #ef4444;
        }

        .pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 2px solid rgba(82,171,152,0.15);
          flex-wrap: wrap;
          gap: 16px;
        }

        .pagination-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .pagination-info {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .page-btn {
          padding: 8px 12px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 8px;
          background: white;
          color: #2b6777;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          min-width: 40px;
          text-align: center;
        }

        .page-btn:hover:not(:disabled) {
          background: rgba(82,171,152,0.1);
          border-color: #52ab98;
          transform: translateY(-1px);
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .page-btn.active {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border-color: #2b6777;
        }

        .rows-per-page {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .rows-select {
          padding: 8px 12px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
          background: white;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 20px;
          max-width: 600px;
          width: 100%;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 24px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(43,103,119,0.08);
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 18px;
          color: #2b6777;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(43,103,119,0.15);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 24px;
        }

        .modal-section {
          margin-bottom: 24px;
        }

        .modal-section-title {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 2px solid rgba(82,171,152,0.2);
        }

        .modal-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .modal-row {
          margin-bottom: 16px;
        }

        .modal-label {
          font-size: 12px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .modal-value {
          font-size: 15px;
          font-weight: 600;
          color: #2b6777;
        }

        .action-modal {
          max-width: 500px;
        }

        .action-modal .modal-body {
          text-align: center;
        }

        .action-type {
          font-size: 18px;
          font-weight: 800;
          margin-bottom: 16px;
          color: ${actionType === 'approve' ? '#22c55e' : '#ef4444'};
        }

        .remarks-input {
          width: 100%;
          padding: 12px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 8px;
          font-family: inherit;
          font-size: 14px;
          margin-bottom: 16px;
          resize: vertical;
          min-height: 80px;
        }

        .remarks-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .modal-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .modal-btn-cancel {
          background: rgba(82,171,152,0.1);
          color: #2b6777;
        }

        .modal-btn-cancel:hover {
          background: rgba(82,171,152,0.2);
        }

        .modal-btn-action {
          background: ${actionType === 'approve' ? '#22c55e' : '#ef4444'};
          color: white;
        }

        .modal-btn-action:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .modal-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }

        .no-data-message {
          text-align: center;
          padding: 60px 20px;
          color: rgba(43,103,119,0.5);
        }

        .no-data-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .no-data-text {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .no-data-subtext {
          font-size: 14px;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-verified {
          background: linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(34,197,94,0.25) 100%);
          color: #22c55e;
        }

        @keyframes glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        @media (max-width: 1024px) {
          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.mobile-open {
            transform: translateX(0);
          }

          .mobile-overlay {
            display: block;
          }

          .sidebar-top {
            display: flex;
          }

          .mobile-menu-toggle {
            display: flex;
          }

          .main-content {
            margin-left: 0;
            width: 100%;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 16px;
          }

          .page-title {
            font-size: 24px;
          }

          .applications-table-container {
            overflow-x: auto;
          }

          .applications-table {
            min-width: 700px;
          }

          .modal-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="dashboard-container">
        <div
          className={`mobile-overlay ${mobileMenuOpen ? 'show' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />

        <div className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setMobileMenuOpen(false)} className="sidebar-close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">{dashboardData?.adminName || 'Admin User'}</div>
                <div className="user-role-sidebar">
                  {dashboardData?.hostelName || 'Hostel Administrator'}
                </div>
              </div>
            </div>
          </div>

          <div className="sidebar-nav">
            {getPrivilegesByCategory('overview').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Overview</div>
                {getPrivilegesByCategory('overview').map((privilege) => (
                  <MenuItem
                    key={privilege.page_id}
                    emoji={privilege.page_icon}
                    label={privilege.page_name}
                    route={privilege.page_route}
                  />
                ))}
              </div>
            )}

            {getPrivilegesByCategory('management').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Management</div>
                {getPrivilegesByCategory('management').map((privilege) => (
                  <MenuItem
                    key={privilege.page_id}
                    emoji={privilege.page_icon}
                    label={privilege.page_name}
                    active={privilege.page_route === 'hostel-admin-leavelevel1'}
                    route={privilege.page_route}
                  />
                ))}
              </div>
            )}

            {getPrivilegesByCategory('approval').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Approval</div>
                {getPrivilegesByCategory('approval').map((privilege) => (
                  <MenuItem
                    key={privilege.page_id}
                    emoji={privilege.page_icon}
                    label={privilege.page_name}
                    route={privilege.page_route}
                  />
                ))}
              </div>
            )}

            {getPrivilegesByCategory('settings').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Settings</div>
                {getPrivilegesByCategory('settings').map((privilege) => (
                  <MenuItem
                    key={privilege.page_id}
                    emoji={privilege.page_icon}
                    label={privilege.page_name}
                    route={privilege.page_route}
                  />
                ))}
              </div>
            )}

            {getPrivilegesByCategory('reports').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Reports</div>
                {getPrivilegesByCategory('reports').map((privilege) => (
                  <MenuItem
                    key={privilege.page_id}
                    emoji={privilege.page_icon}
                    label={privilege.page_name}
                    route={privilege.page_route}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        <div className="main-content">
          <div className="top-header">
            <div className="header-left">
              <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(true)}>☰</button>
              <div className="header-title-section">
                <div className="header-greeting">Welcome back</div>
                <div className="header-name">{dashboardData?.adminName || 'Admin'}</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>

          <div className="page-title-section">
            <div className="page-title-content">
              <div className="page-title">Leave Applications - Level 1</div>
              <div className="page-subtitle">Review and approve student leave applications</div>
            </div>
          </div>

          <div className="search-section">
            <input
              type="text"
              className="search-input"
              placeholder="Search by student name, roll number, or leave type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="applications-table-container">
            <div className="table-header">
              <div className="table-title">Applications ({filteredApplications.length})</div>
              <div className="table-count">
                Showing {currentPageApplications.length} of {filteredApplications.length}
              </div>
            </div>

            {filteredApplications.length === 0 ? (
              <div className="no-data-message">
                <div className="no-data-icon">📋</div>
                <div className="no-data-text">No Leave Applications Found</div>
                <div className="no-data-subtext">All applications have been processed or no applications match your search.</div>
              </div>
            ) : (
              <>
                <table className="applications-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Leave Type</th>
                      <th>Duration</th>
                      <th>Applied Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageApplications.map((app) => (
                      <tr key={app.id}>
                        <td>
                          <div className="student-info">
                            <div className="student-avatar">
                              {app.student_name ? app.student_name.charAt(0).toUpperCase() : 'S'}
                            </div>
                            <div className="student-details">
                              <div className="student-name">{app.student_name || 'N/A'}</div>
                              <div className="student-id">{app.roll_number || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td>{app.leave_type || 'N/A'}</td>
                        <td>{formatDate(app.from_date)} - {formatDate(app.to_date)}</td>
                        <td>{formatDate(app.applied_date)}</td>
                        <td>
                          <span className="status-badge status-verified">Pending</span>
                        </td>
                        <td>
                          <button
                            className="action-btn"
                            onClick={() => viewApplicationDetails(app)}
                          >
                            View
                          </button>
                          <button
                            className="action-btn approve-btn"
                            onClick={() => openActionModal(app, 'approve')}
                          >
                            Approve
                          </button>
                          <button
                            className="action-btn reject-btn"
                            onClick={() => openActionModal(app, 'reject')}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="pagination">
                  <div className="pagination-left">
                    <div className="rows-per-page">
                      <label>Rows per page:</label>
                      <select
                        className="rows-select"
                        value={rowsPerPage}
                        onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                    <div className="pagination-info">
                      Page {currentPage} of {totalPages}
                    </div>
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="page-btn"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        className={`page-btn ${page === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="page-btn"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {detailsModalOpen && selectedApplication && (
          <div className="modal-overlay" onClick={() => setDetailsModalOpen(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">Application Details</div>
                <button className="modal-close" onClick={() => setDetailsModalOpen(false)}>×</button>
              </div>
              <div className="modal-body">

                {/* Student Information Section */}
                <div className="modal-section">
                  <div className="modal-section-title">Student Information</div>
                  <div className="modal-grid">
                    <div className="modal-row">
                      <div className="modal-label">Name</div>
                      <div className="modal-value">{selectedApplication.student_name || 'N/A'}</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Roll Number</div>
                      <div className="modal-value">{selectedApplication.roll_number || 'N/A'}</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Email</div>
                      <div className="modal-value">{selectedApplication.email || 'N/A'}</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Phone</div>
                      <div className="modal-value">{selectedApplication.phone || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Leave Details Section */}
                <div className="modal-section">
                  <div className="modal-section-title">Leave Details</div>
                  <div className="modal-grid">
                    <div className="modal-row">
                      <div className="modal-label">Leave Type</div>
                      <div className="modal-value">{selectedApplication.leave_type || 'N/A'}</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">From Date</div>
                      <div className="modal-value">{formatDate(selectedApplication.from_date)}</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">To Date</div>
                      <div className="modal-value">{formatDate(selectedApplication.to_date)}</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Duration</div>
                      <div className="modal-value">{selectedApplication.total_days || 0} days</div>
                    </div>
                    <div className="modal-row">
                      <div className="modal-label">Applied Date</div>
                      <div className="modal-value">{formatDate(selectedApplication.applied_date)}</div>
                    </div>
                  </div>
                </div>

                {/* Reason Section */}
                <div className="modal-section">
                  <div className="modal-section-title">Reason</div>
                  <div className="modal-value">{selectedApplication.reason || 'N/A'}</div>
                </div>

                {/* 🆕 ATTACHMENT SECTION - NEW */}
                {selectedApplication.attachment_path && selectedApplication.attachment_original_name && (
                  <div className="modal-section">
                    <div className="modal-section-title">📎 Attachment</div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '32px',
                        color: '#6b7280'
                      }}>
                        📄
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '4px'
                        }}>
                          {selectedApplication.attachment_original_name}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          Student uploaded document
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleDownloadAttachment(
                            selectedApplication.id,
                            selectedApplication.attachment_original_name
                          )}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                        >
                          <span>📥</span>
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Parent Approval Info */}
                {selectedApplication.parent_remarks && (
                  <div className="modal-section">
                    <div className="modal-section-title">Parent Approval</div>
                    <div className="modal-grid">
                      <div className="modal-row">
                        <div className="modal-label">Status</div>
                        <div className="modal-value" style={{ color: '#10b981', fontWeight: '600' }}>
                          ✓ Approved
                        </div>
                      </div>
                      <div className="modal-row">
                        <div className="modal-label">Approved At</div>
                        <div className="modal-value">{selectedApplication.parent_approved_at || 'N/A'}</div>
                      </div>
                      <div className="modal-row">
                        <div className="modal-label">Remarks</div>
                        <div className="modal-value">{selectedApplication.parent_remarks}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Modal Actions */}
                <div className="modal-actions">
                  <button
                    className="modal-btn modal-btn-cancel"
                    onClick={() => setDetailsModalOpen(false)}
                  >
                    Close
                  </button>
                  <button
                    className="modal-btn modal-btn-action"
                    onClick={() => openActionModal(selectedApplication, 'approve')}
                  >
                    Approve
                  </button>
                  <button
                    className="modal-btn modal-btn-action"
                    style={{ background: '#ef4444' }}
                    onClick={() => openActionModal(selectedApplication, 'reject')}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {actionModalOpen && selectedApplication && (
          <div className="modal-overlay" onClick={closeActionModal}>
            <div className="modal action-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">Confirm Action</div>
                <button className="modal-close" onClick={closeActionModal}>×</button>
              </div>
              <div className="modal-body">
                <div className="action-type">
                  {actionType === 'approve' ? 'Approve' : 'Reject'} Application
                </div>
                <p>Are you sure you want to {actionType} this leave application?</p>
                <textarea
                  className="remarks-input"
                  placeholder="Enter remarks (required)"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
                <div className="modal-actions">
                  <button className="modal-btn modal-btn-cancel" onClick={closeActionModal}>Cancel</button>
                  <button
                    className="modal-btn modal-btn-action"
                    onClick={handleAction}
                    disabled={processing || !remarks.trim()}
                  >
                    {processing ? 'Processing...' : actionType === 'approve' ? 'Approve' : 'Reject'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HostelLeaveLevel1;
